import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Heart, Search } from "lucide-react";
import { useState } from "react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const nav = [
    { to: "/", label: "Accueil" },
    { to: "/shop", label: "Boutique" },
    { to: "/about", label: "Mon Histoire" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <div className="min-h-screen text-[#1F1C18]">
      {/* Marquee */}
      <div className="bg-patchwork text-white overflow-hidden py-2 border-b border-[#E5E1D8]">
        <div className="flex whitespace-nowrap animate-marquee text-xs uppercase tracking-[0.25em]">
          {Array.from({ length: 6 }).map((_, idx) => (
            <span key={idx} className="mx-6 flex items-center gap-6">
              <span>100% Fait Main</span>
              <span className="text-[#FFCF66]">✦</span>
              <span>Pièces Uniques</span>
              <span className="text-[#FFCF66]">✦</span>
              <span>Laines Naturelles</span>
              <span className="text-[#FFCF66]">✦</span>
              <span>Matières OEKO-TEX</span>
              <span className="text-[#FFCF66]">✦</span>
              <span>Artisanat Éthique</span>
              <span className="text-[#FFCF66]">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#F7F5F0]/85 backdrop-blur-xl border-b border-[#E5E1D8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-20 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <span className="font-serif-display text-3xl font-semibold text-terracotta tracking-tight">
                Labounite
              </span>
              <span className="font-accent text-2xl text-moss -rotate-3">shop</span>
            </Link>

            <nav className="hidden md:flex items-center gap-10">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `text-sm uppercase tracking-[0.18em] transition-colors ${
                      isActive ? "text-terracotta" : "text-[#1F1C18] hover:text-terracotta"
                    }`
                  }
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              {user ? (
                <button
                  className="hidden md:flex items-center gap-2 text-sm hover:text-terracotta"
                  onClick={() => navigate(user.role === "admin" ? "/admin" : "/account")}
                  data-testid="account-btn"
                >
                  <User size={18} strokeWidth={1.5} />
                  <span className="hidden lg:inline">{user.first_name || user.name?.split(" ")[0]}</span>
                </button>
              ) : (
                <Link
                  to="/login"
                  className="hidden md:flex items-center gap-2 text-sm hover:text-terracotta"
                  data-testid="login-link"
                >
                  <User size={18} strokeWidth={1.5} />
                  <span>Connexion</span>
                </Link>
              )}
              <Link
                to="/cart"
                className="relative flex items-center gap-2 text-sm hover:text-terracotta"
                data-testid="cart-link"
              >
                <ShoppingBag size={20} strokeWidth={1.5} />
                {count > 0 && (
                  <span
                    className="absolute -top-2 -right-2 bg-terracotta text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center"
                    data-testid="cart-count"
                  >
                    {count}
                  </span>
                )}
              </Link>
              <button
                className="md:hidden p-2"
                onClick={() => setOpen(!open)}
                data-testid="menu-toggle"
              >
                {open ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-[#E5E1D8] bg-cream">
            <div className="px-6 py-4 space-y-3">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="block text-base"
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </NavLink>
              ))}
              {user ? (
                <>
                  <button onClick={() => { setOpen(false); navigate(user.role === "admin" ? "/admin" : "/account"); }} className="block text-base" data-testid="mobile-account-btn">Mon Compte</button>
                  <button onClick={() => { logout(); setOpen(false); }} className="block text-base text-terracotta" data-testid="mobile-logout-btn">Déconnexion</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="block text-base" data-testid="mobile-login-link">Connexion</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-patchwork text-white mt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <span className="font-serif-display text-3xl drop-shadow-md">Labounite</span>
              <span className="font-accent text-2xl text-white -rotate-3 drop-shadow">shop</span>
            </div>
            <p className="font-body text-sm leading-relaxed text-white/90 max-w-md drop-shadow-sm">
              Labounite perpétue depuis deux générations l’art du crochet et du tricot, à travers des créations artisanales uniques, réalisées avec passion, patience et délicatesse.
            </p>
            <p className="font-accent text-3xl text-white mt-6 -rotate-2 drop-shadow-md">Fait avec passion ♡</p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white mb-4 drop-shadow">Boutique</h4>
            <ul className="space-y-2 text-sm text-white/90">
              <li><Link to="/shop" className="hover:text-[#FFCF66] transition">Toutes les pièces</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white mb-4 drop-shadow">Maison</h4>
            <ul className="space-y-2 text-sm text-white/90">
              <li><Link to="/about" className="hover:text-[#FFCF66] transition">Mon histoire</Link></li>
              <li><Link to="/contact" className="hover:text-[#FFCF66] transition" data-testid="footer-contact-link">Contact</Link></li>
              <li><Link to="/login" className="hover:text-[#FFCF66] transition">Espace client</Link></li>
              <li><Link to="/mentions-legales" className="hover:text-[#FFCF66] transition" data-testid="footer-legal-link">Mentions légales</Link></li>
              <li><Link to="/cgv" className="hover:text-[#FFCF66] transition" data-testid="footer-cgv-link">CGV</Link></li>
              <li><Link to="/cookies" className="hover:text-[#FFCF66] transition" data-testid="footer-cookies-link">Politique de cookies</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/20 py-6 text-center text-xs text-white/70">
          © 2026 LabouniteShop — Tous droits réservés
        </div>
      </footer>
    </div>
  );
}
